const parseObject = (obj) => {
  Object.keys(obj).forEach((key) => {
    const value = obj[key]
    if (typeof value === 'string') {
      if (value.startsWith('!!js/regexp ')) {
        const fragments = value.match(/!!js\/regexp \/(.*?)\/([gimy])?$/)
        Object.assign(obj, { [key]: new RegExp(fragments?.[1] || '', fragments?.[2] || '') })
      } else if (value.startsWith('!!js/greaterThan ')) {
        const fragments = value.match(/!!js\/greaterThan (\d+)$/)
        Object.assign(obj, {
          [key]: {
            compare: (newVal) => Number(newVal) > (Number(fragments?.[1]) || 0),
          },
        })
      } else if (value.startsWith('!!js/range ')) {
        const fragments = value.match(/!!js\/range (\d+)-(\d+)$/)
        Object.assign(obj, {
          [key]: {
            compare: (newVal) =>
              Number(newVal) >= (Number(fragments?.[1]) || 0) && Number(newVal) <= (Number(fragments?.[2]) || 0),
          },
        })
      } else {
        Object.assign(obj, { [key]: value })
      }
    }
  })
  return obj
}

const parseAsFeature = (data) => {
  const feature = {
    enabled: false,
  }

  feature.value = data

  if (typeof data === 'boolean') {
    feature.enabled = data
  }
  if (typeof data === 'string') {
    if (data && data.toLowerCase() !== 'off') {
      feature.enabled = true
    }
  }
  if (typeof data === 'number') {
    feature.enabled = true
  }

  if (data && typeof data === 'object') {
    feature.enabled = false

    if ('enabled' in data) {
      feature.enabled = !!data.enabled || false
    }

    if ('access' in data && Array.isArray(data.access)) {
      feature.access = data.access.map(parseObject)
    }
    if ('companies' in data && Array.isArray(data.companies)) {
      feature.companies = data.companies.map(parseObject)
    }
    if ('interval' in data && data.interval) {
      feature.interval = data.interval
    }
    if ('users' in data && Array.isArray(data.users)) {
      feature.users = data.users.map(parseObject)
    }

    feature.value = 'value' in data ? data.value : undefined
  }

  return feature
}

const testExpressionOrString = (expression, comparedTo) => {
  if (expression === null || expression === undefined || comparedTo === null || comparedTo === undefined) {
    return false
  }

  if (expression) {
    if (expression.test) {
      return expression.test(comparedTo)
    }

    if (expression.compare) {
      return expression.compare(comparedTo)
    }
  }

  return `${expression}` === `${comparedTo}`
}

const matchesUser = (featUser, user) =>
  !!user &&
  (testExpressionOrString(featUser.email, user.email) ||
    testExpressionOrString(featUser.username, user.username) ||
    testExpressionOrString(featUser.id, user.id))

const matchesCompany = (featCompany, company) =>
  !!company &&
  (testExpressionOrString(featCompany.id, company.id) ||
    testExpressionOrString(featCompany.email, company.email) ||
    testExpressionOrString(featCompany.tipoEmpresa, company.tipoEmpresa))

const matchesAccess = (featAccess, access) =>
  access &&
  ((access.level && !!(featAccess.level & access.level)) || testExpressionOrString(featAccess.scope, access.scope))

class Features {
  addCallback = ({ callback, feature, validator }) => {
    if (validator(feature)) {
      callback()
    } else {
      this.callbacks[feature] = this.callbacks[feature] || []
      this.callbacks[feature].push({
        callback,
        validator,
      })
    }
  }

  callbacks = {}

  checkCallbacks = () => {
    Object.keys(this.callbacks).forEach((feature) => {
      this.callbacks[feature].filter(({ validator }) => validator()).forEach(({ callback }) => callback())
      this.callbacks[feature] = this.callbacks[feature].filter(({ validator }) => !validator())
    })
  }

  enabled = false

  features = {}

  getCompany = Function.prototype

  getUser = Function.prototype

  isEnabled = (feature, callback = null) => {
    if (callback) {
      this.addCallback({
        callback,
        feature,
        validator: () => this.isEnabled(feature),
      })
    }

    return (
      !this.enabled ||
      !!this.features[feature]?.enabled ||
      this.isEnabledInInterval(feature) ||
      this.isEnabledForUser(feature, this.getUser()) ||
      this.isEnabledForCompany(feature, this.getCompany())
    )
  }

  isEnabledForCompany = (feature, company, callback = null) => {
    if (!this.enabled) {
      return true
    }

    if (callback) {
      this.addCallback({
        callback,
        feature,
        validator: () => this.isEnabledForCompany(feature, company),
      })
    }

    if (company && this.features[feature]) {
      const { companies } = this.features[feature]
      if (companies?.length) {
        for (const featCompany of companies) {
          if (matchesCompany(featCompany, company)) {
            return true
          }
        }
      } else {
        return this.features[feature].enabled
      }
    }
    return false
  }

  isEnabledForUser = (feature, user, callback = null) => {
    if (!this.enabled) {
      return true
    }

    if (callback) {
      this.addCallback({
        callback,
        feature,
        validator: () => this.isEnabledForUser(feature, user),
      })
    }

    if (user && this.features[feature]) {
      const { users } = this.features[feature]
      if (users?.length) {
        for (const featUser of users) {
          if (matchesUser(featUser, user)) {
            return true
          }
        }
      } else {
        return this.features[feature].enabled
      }
    }
    return false
  }

  isEnabledInInterval = (feature, callback = null) => {
    if (!this.enabled) {
      return true
    }

    if (callback) {
      this.addCallback({
        callback,
        feature,
        validator: () => this.isEnabledInInterval(feature),
      })
    }

    if (this.features[feature]) {
      const { interval } = this.features[feature]
      if (interval && (interval.since || interval.until)) {
        const now = new Date()
        if (interval.since) {
          const enabled = now >= new Date(interval.since)
          return enabled && (!interval.until || now <= new Date(interval.until))
        }
        return !!interval.until && now <= new Date(interval.until)
      }
      return this.features[feature].enabled
    }
    return false
  }

  isEnabledWithAccess = (feature, userAccess, callback = null) => {
    if (callback) {
      this.addCallback({
        callback,
        feature,
        validator: () => this.isEnabledWithAccess(feature, userAccess),
      })
    }

    if (!this.enabled) {
      return true
    }

    if (userAccess && this.features[feature]) {
      const { access } = this.features[feature]
      if (access?.length) {
        for (const featAccess of access) {
          if (matchesAccess(featAccess, userAccess)) {
            return true
          }
        }
      } else {
        return this.features[feature].enabled
      }
    }
    return false
  }

  resetFeatures = () => {
    this.features = {}
  }

  set = (object) => {
    Object.keys(object).forEach((name) => {
      this.features[name] = parseAsFeature(object[name])
    })
    this.enabled = !!this.features.feature_flags?.enabled
    this.checkCallbacks()
  }

  setCompanyFunction = (func) => {
    this.getCompany = func
    this.checkCallbacks()
  }

  setUserFunction = (func) => {
    this.getUser = func
    this.checkCallbacks()
  }

  value = (feature, defaultValue = undefined) => (this.isEnabled(feature) ? this.features[feature].value : defaultValue)

  valueForUser = (feature, user, defaultValue) => {
    if (this.enabled && !!this.features[feature]) {
      const { users } = this.features[feature]
      if (users?.length) {
        for (const featUser of users) {
          if (matchesUser(featUser, user)) {
            return featUser.value === undefined ? defaultValue : featUser.value
          }
        }
      } else {
        return this.features[feature].value === undefined ? defaultValue : this.features[feature].value
      }
    }
    return defaultValue
  }
}

export default Features
